import PropertyFields, {
  BusinessFieldMappings,
  resolveFieldMapping,
} from "@/constants/propertyFields";
import { selectCustomFields } from "@/features/survey/surveySelectors";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldModal } from "@emerald-react/modal";
import { EmeraldNotification } from "@emerald-react/notification";
import { EmeraldSelect } from "@emerald-react/select";
import { EmeraldStepperV2 } from "@emerald-react/stepper-v2";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

interface ColumnMatchingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNext: (mappings: Record<string, string>) => void;
  importResult: {
    matched: string[];
    notMatched: string[];
    unParsedColumns: string[][];
  } | null;
  isProcessing?: boolean;
}

// Helper function to check if a column name is blank or empty
const isBlankColumn = (columnName: string): boolean => {
  return (
    !columnName ||
    columnName.trim() === "" ||
    columnName.toLowerCase() === "column" ||
    /^column\s*\d*$/i.test(columnName.trim())
  );
};

// Component to render column name with warning for blank columns
const ColumnNameDisplay = ({ columnName }: { columnName: string }) => {
  if (isBlankColumn(columnName)) {
    return (
      <div className="flex items-center gap-2">
        <EmeraldIcon
          variant={"icon"}
          icon={"warning"}
          type={"filled"}
          fontsizeInRem={1.5}
          className="text-error-icon"
        />
        <span className="text-sm text-error-icon font-medium">
          Column was blank
        </span>
      </div>
    );
  }

  return <div className="text-sm text-data">{columnName}</div>;
};

// Simplified component using EmeraldSelect
const SearchableSelectWithClear = ({
  column,
  value,
  options,
  onChange,
}: {
  column: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (column: string, value: string) => void;
  onClear: (column: string) => void;
}) => {
  const handleSelectChange = (_: any, selectedValue: string | string[]) => {
    if (typeof selectedValue === "string") {
      if (selectedValue === "") {
        // When cleared via the clear icon
        if (value === "DONT_IMPORT") {
          // Second clear click - set to empty to open search input
          onChange(column, "search..");
        } else {
          // First clear click - set to "Don't import"
          onChange(column, "DONT_IMPORT");
        }
      } else {
        onChange(column, selectedValue);
      }
    }
  };

  return (
    <EmeraldSelect
      clearable
      label=""
      placeholder="Search..."
      value={value || ""}
      options={options}
      onChange={handleSelectChange}
      searchable
      size="small"
    />
  );
};

const ColumnMatchingModal = ({
  isOpen,
  onClose,
  onNext,
  importResult,
  isProcessing = false,
}: ColumnMatchingModalProps) => {
  const [columnMappings, setColumnMappings] = useState<Record<string, string>>(
    {},
  );
  const [clearedColumns, setClearedColumns] = useState<Set<string>>(new Set());
  const [originalMatchedColumns, setOriginalMatchedColumns] = useState<
    string[]
  >([]);
  const [originalUnmatchedColumns, setOriginalUnmatchedColumns] = useState<
    string[]
  >([]);

  // Get custom fields from Redux
  const customFields = useSelector(selectCustomFields);

  // Convert PropertyFields to dropdown options - memoized to prevent re-creation
  const availableTargetColumns = useMemo(() => {
    // Filter out unwanted fields: flyer, site plan, and photos
    const excludedFields = ["flyer", "siteplan", "sitePlan", "photos", "photo"];

    return PropertyFields.filter(
      (field) =>
        !excludedFields.some(
          (excluded) =>
            field.key.toLowerCase().includes(excluded.toLowerCase()) ||
            field.label.toLowerCase().includes(excluded.toLowerCase()),
        ),
    )
      .map((field) => ({
        value: field.key,
        label: field.label,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically by label
  }, []);

  // Add custom field options dynamically from Redux - memoized
  const customFieldOptions = useMemo(() => {
    return customFields
      .map((field, index) => {
        const label = field.name || `Custom Field ${index + 1}`;
        const value = field.name || `Custom Field ${index + 1}`; // Use field name directly

        return { value, label };
      })
      .sort((a, b) => a.label.localeCompare(b.label)); // Sort custom fields alphabetically
  }, [customFields]);

  // Combine all options and sort alphabetically - memoized
  const allTargetColumns = useMemo(() => {
    const emptyOption = { value: "search..", label: "Search..." };
    const dontImportOption = { value: "DONT_IMPORT", label: "Don't import" };
    const customFieldImportOption = {
      value: "CUSTOM_FIELD_IMPORT",
      label: "Import as Custom Field",
    };

    // Combine all property fields and custom fields, then sort alphabetically
    const combinedFields = [
      ...availableTargetColumns,
      ...customFieldOptions,
    ].sort((a, b) => a.label.localeCompare(b.label));

    return [
      emptyOption,
      dontImportOption,
      customFieldImportOption,
      ...combinedFields,
    ];
  }, [availableTargetColumns, customFieldOptions]);

  // Get already selected values to prevent duplicates
  const selectedValues = useMemo(() => {
    const values = Object.values(columnMappings).filter(
      (value) =>
        value && value !== "DONT_IMPORT" && value !== "CUSTOM_FIELD_IMPORT",
    );
    return new Set(values);
  }, [columnMappings]);

  // Function to find the best match for a column name - memoized with useCallback
  const findMatchedField = useCallback(
    (columnName: string) => {
      // Always return "DONT_IMPORT" for blank columns
      if (isBlankColumn(columnName)) {
        return "DONT_IMPORT";
      }

      const lowerColumnName = columnName.toLowerCase();

      // Special handling for common latitude/longitude variations
      if (
        lowerColumnName.includes("lat") &&
        (lowerColumnName.includes("latitude") ||
          lowerColumnName === "lat" ||
          lowerColumnName.startsWith("lat ") ||
          lowerColumnName.endsWith(" lat")) &&
        !lowerColumnName.includes("platform") &&
        !lowerColumnName.includes("escalation") && // ✅ Explicitly excludes escalation
        !lowerColumnName.includes("translation") && // ✅ Prevents other false positives
        !lowerColumnName.includes("relation")
      ) {
        const latitudeField = PropertyFields.find(
          (field) => field.key === "latitude",
        );
        if (latitudeField && !selectedValues.has(latitudeField.key)) {
          return latitudeField.key;
        }
      }

      if (
        lowerColumnName.includes("lng") ||
        lowerColumnName.includes("lon") ||
        (lowerColumnName.includes("long") &&
          !lowerColumnName.includes("belong"))
      ) {
        const longitudeField = PropertyFields.find(
          (field) => field.key === "longitude",
        );
        if (longitudeField && !selectedValues.has(longitudeField.key)) {
          return longitudeField.key;
        }
      }

      // Special handling for zip/postal code variations
      if (
        lowerColumnName.includes("zip") ||
        lowerColumnName.includes("postal")
      ) {
        const zipCodeField = PropertyFields.find(
          (field) => field.key === "postalCode",
        );
        if (zipCodeField && !selectedValues.has(zipCodeField.key)) {
          return zipCodeField.key;
        }
      }

      // Special handling for trailer parking variations
      if (
        lowerColumnName.includes("trailer") &&
        lowerColumnName.includes("parking")
      ) {
        const trailerParkingField = PropertyFields.find(
          (field) => field.key === "parkingSpacesTrailers",
        );
        if (
          trailerParkingField &&
          !selectedValues.has(trailerParkingField.key)
        ) {
          return trailerParkingField.key;
        }
      }

      // Special handling for car parking variations
      if (
        lowerColumnName.includes("car") &&
        lowerColumnName.includes("parking")
      ) {
        const carParkingField = PropertyFields.find(
          (field) => field.key === "parkingSpacesCars",
        );
        if (carParkingField && !selectedValues.has(carParkingField.key)) {
          return carParkingField.key;
        }
      }

      // Check business field mappings for exact matches
      const businessFieldMatch = Object.entries(BusinessFieldMappings).find(
        ([businessHeader]) =>
          businessHeader.toLowerCase() === lowerColumnName ||
          columnName === businessHeader,
      );

      if (businessFieldMatch) {
        const [, targetField] = businessFieldMatch;
        // Check if the target field exists in PropertyFields and isn't already selected
        const propertyField = PropertyFields.find(
          (field) => field.key === targetField,
        );
        if (propertyField && !selectedValues.has(propertyField.key)) {
          return propertyField.key;
        }
      }

      // First, try exact match with custom field labels (case-insensitive)
      const exactCustomFieldMatch = customFieldOptions.find(
        (option) =>
          option.label.toLowerCase() === lowerColumnName &&
          !selectedValues.has(option.value),
      );

      if (exactCustomFieldMatch) {
        return exactCustomFieldMatch.value;
      }

      // Try to match custom fields with patterns like "Custom Field 1", "Custom Field 3", etc.
      const customFieldMatch = columnName.match(/^custom\s+field\s+(\d+)$/i);
      if (customFieldMatch) {
        const fieldNumber = parseInt(customFieldMatch[1], 10);

        // Try to find a custom field that has this number in its label
        const matchingCustomField = customFieldOptions.find((option) => {
          const labelMatch = option.label.match(/custom\s+field\s+(\d+)/i);
          if (labelMatch) {
            const optionNumber = parseInt(labelMatch[1], 10);
            return (
              optionNumber === fieldNumber && !selectedValues.has(option.value)
            );
          }
          return false;
        });

        if (matchingCustomField) {
          return matchingCustomField.value;
        }
      }

      // Prevent specific business fields from matching with PropertyFields
      const excludedBusinessFields = [
        "Year Built / Renovated",
        "Asking Rate (Shell/Office)",
      ];
      const isExcludedBusinessField = excludedBusinessFields.some(
        (excludedField) =>
          columnName.toLowerCase() === excludedField.toLowerCase(),
      );

      // Try to find exact or partial matches with property fields (but exclude specific business fields)
      const exactMatch = !isExcludedBusinessField
        ? PropertyFields.find(
            (field) =>
              field.label.toLowerCase() === lowerColumnName ||
              field.key.toLowerCase() === lowerColumnName,
          )
        : null;

      if (exactMatch && !selectedValues.has(exactMatch.key)) {
        return exactMatch.key;
      }

      // Try partial matches with property fields (but exclude specific business fields)
      const partialMatch = !isExcludedBusinessField
        ? PropertyFields.find(
            (field) =>
              (field.label.toLowerCase().includes(lowerColumnName) ||
                lowerColumnName.includes(field.label.toLowerCase()) ||
                field.key.toLowerCase().includes(lowerColumnName) ||
                lowerColumnName.includes(field.key.toLowerCase())) &&
              !selectedValues.has(field.key),
          )
        : null;

      if (partialMatch) {
        return partialMatch.key;
      }

      // Try to match custom fields by partial name
      const partialCustomFieldMatch = customFieldOptions.find(
        (option) =>
          option.label.toLowerCase().includes(lowerColumnName) &&
          !selectedValues.has(option.value),
      );

      if (partialCustomFieldMatch) {
        return partialCustomFieldMatch.value;
      }

      // If no unique match found, return "Import as Custom Field" for non-blank columns
      // and "Don't import" for blank columns
      return "CUSTOM_FIELD_IMPORT";
    },
    [selectedValues, customFieldOptions],
  );

  // Helper function to get the current value for a column
  const getColumnValue = useCallback(
    (column: string) => {
      // If user has manually set a mapping, use that
      if (columnMappings[column]) {
        return columnMappings[column];
      }

      // If column was cleared by user, return "DONT_IMPORT"
      if (clearedColumns.has(column)) {
        return "DONT_IMPORT";
      }

      // Otherwise, use automatic matching
      return findMatchedField(column);
    },
    [columnMappings, clearedColumns, findMatchedField],
  );

  // Helper function to check if a column should be excluded
  const isExcludedColumn = useCallback((columnName: string) => {
    const lowerColumnName = columnName.toLowerCase();
    const excludedFields = [
      "flyer",
      "siteplan",
      "sitePlan",
      "photos",
      "photo",
      "site plan",
      "site_plan",
    ];
    return excludedFields.some((excluded) =>
      lowerColumnName.includes(excluded.toLowerCase()),
    );
  }, []);

  // Modified: Use local state for section assignment to ensure stability
  const filteredColumnCategories = useMemo(() => {
    return {
      matched: originalMatchedColumns.filter(
        (column) => !isExcludedColumn(column),
      ),
      notMatched: originalUnmatchedColumns.filter(
        (column) => !isExcludedColumn(column),
      ),
    };
  }, [originalMatchedColumns, originalUnmatchedColumns, isExcludedColumn]);

  // Check if required fields are mapped for enabling Next button
  const isNextEnabled = useMemo(() => {
    if (!importResult) return false;

    // Get all columns from the import result
    const allColumns = [...importResult.matched, ...importResult.notMatched];

    // Helper function to check if a field is mapped
    const isFieldMapped = (fieldKey: string) => {
      // Check manual mappings
      if (Object.values(columnMappings).includes(fieldKey)) {
        return true;
      }
      // Check auto-mappings
      return allColumns.some((column) => {
        const mapping = columnMappings[column] || findMatchedField(column);
        return mapping === fieldKey;
      });
    };

    // Check for Address Line 1, City, State OR Latitude and Longitude
    const addressLine1Mapped = isFieldMapped("address1");
    const cityMapped = isFieldMapped("city");
    const stateMapped = isFieldMapped("stateProvince");
    const latitudeMapped = isFieldMapped("latitude");
    const longitudeMapped = isFieldMapped("longitude");

    const addressSetMapped = addressLine1Mapped && cityMapped && stateMapped;
    const latLongSetMapped = latitudeMapped && longitudeMapped;

    return addressSetMapped || latLongSetMapped;
  }, [importResult, columnMappings, findMatchedField]);

  // Get available options for a specific column (all options are now selectable)
  const getAvailableOptions = useCallback(() => {
    // Return all options since user can select any option and it will override previous selections
    return allTargetColumns;
  }, [allTargetColumns]);

  const handleColumnMapping = useCallback(
    (sourceColumn: string, targetColumn: string) => {
      // Remove from cleared/conflicted columns if user selects a new mapping
      if (targetColumn !== "DONT_IMPORT") {
        setClearedColumns((prev) => {
          const newSet = new Set(prev);
          newSet.delete(sourceColumn);
          return newSet;
        });
      }

      setColumnMappings((prev) => {
        const newMappings = { ...prev };

        // If selecting "Don't import" or "Import as Custom Field", just set it directly without conflict checking
        if (
          targetColumn === "DONT_IMPORT" ||
          targetColumn === "CUSTOM_FIELD_IMPORT"
        ) {
          newMappings[sourceColumn] = targetColumn;
          return newMappings;
        }

        // Check for conflicts with manually set mappings (only for actual field mappings, not CUSTOM_FIELD_IMPORT)
        Object.entries(newMappings).forEach(([column, value]) => {
          if (
            column !== sourceColumn &&
            value === targetColumn &&
            value !== "CUSTOM_FIELD_IMPORT"
          ) {
            // Set the previously mapped column to "Don't import"
            newMappings[column] = "DONT_IMPORT";
          }
        });

        // Check for conflicts with auto-matched columns in matched section that haven't been manually overridden
        if (importResult) {
          importResult.matched.forEach((column) => {
            // Skip if it's the current column being updated, or if it's already manually overridden, or if it's excluded
            if (
              column === sourceColumn ||
              newMappings.hasOwnProperty(column) ||
              isExcludedColumn(column)
            ) {
              return;
            }

            // Get the auto-matched value for this column
            const autoMatchedValue = findMatchedField(column);

            // If this auto-matched column conflicts with the new selection, override it to "DONT_IMPORT"
            if (
              autoMatchedValue === targetColumn &&
              autoMatchedValue !== "DONT_IMPORT" &&
              autoMatchedValue !== "CUSTOM_FIELD_IMPORT"
            ) {
              newMappings[column] = "DONT_IMPORT";
            }
          });
        }

        // Set the new mapping
        newMappings[sourceColumn] = targetColumn;

        return newMappings;
      });
    },
    [findMatchedField, importResult, isExcludedColumn],
  );

  // Handle clearing a column mapping - enhanced to handle "Don't import" clearing
  const handleClearMapping = useCallback(
    (sourceColumn: string) => {
      // If the current value is "DONT_IMPORT", completely clear the mapping
      const currentValue = getColumnValue(sourceColumn);

      if (currentValue === "DONT_IMPORT") {
        // Remove from all tracking sets and column mappings to allow auto-matching
        setClearedColumns((prev) => {
          const newSet = new Set(prev);
          newSet.delete(sourceColumn);
          return newSet;
        });
        setColumnMappings((prev) => {
          const newMappings = { ...prev };
          delete newMappings[sourceColumn];
          return newMappings;
        });
      } else {
        // For non "Don't import" values, add to cleared columns set to keep in matched section
        // Default to "Import as Custom Field" for non-blank columns, "Don't import" for blank ones
        const defaultValue = isBlankColumn(sourceColumn)
          ? "DONT_IMPORT"
          : "CUSTOM_FIELD_IMPORT";
        setClearedColumns((prev) => new Set(prev).add(sourceColumn));
        setColumnMappings((prev) => ({
          ...prev,
          [sourceColumn]: defaultValue,
        }));
      }
    },
    [getColumnValue],
  );

  const handleNext = useCallback(() => {
    // Get all columns from both matched and unmatched sections
    const allColumns = [
      ...filteredColumnCategories.matched,
      ...filteredColumnCategories.notMatched,
    ];

    // Convert mappings for API payload - include all columns with their current values
    const processedMappings = Object.fromEntries(
      allColumns
        .map((header) => {
          let value = getColumnValue(header);
          if (value === "DONT_IMPORT") {
            return [header, "NO_MATCH"];
          }
          if (value === "CUSTOM_FIELD_IMPORT") {
            return [header, "custom"];
          }

          // Apply PropertyField priority logic to resolve the final mapping
          if (value && value !== "NO_MATCH" && value !== "custom") {
            value = resolveFieldMapping(header, value);
          }

          return [header, value || "NO_MATCH"];
        })
        .filter(([, value]) => value), // Only filter out completely empty values
    );
    onNext(processedMappings);
  }, [filteredColumnCategories, getColumnValue, onNext]);

  const handleCancel = useCallback(() => {
    setColumnMappings({});
    onClose();
  }, [onClose]);

  // Reset all states when new import results are received
  useEffect(() => {
    if (importResult) {
      setClearedColumns(new Set());
      setColumnMappings({});

      // Process all columns to determine proper sections
      const allColumns = [...importResult.matched, ...importResult.notMatched];
      const processedMatched: string[] = [];
      const processedUnmatched: string[] = [];

      allColumns.forEach((column) => {
        // Check if column should be excluded
        const lowerColumnName = column ? column.toLowerCase() : "";
        const excludedFields = [
          "flyer",
          "siteplan",
          "sitePlan",
          "photos",
          "photo",
          "site plan",
          "site_plan",
        ];
        const isExcluded = excludedFields.some((excluded) =>
          lowerColumnName.includes(excluded.toLowerCase()),
        );

        if (isExcluded) return;

        // Check for blank columns
        if (
          !column ||
          column.trim() === "" ||
          lowerColumnName === "blank" ||
          lowerColumnName === "column" ||
          /^column\s*\d*$/i.test(column.trim())
        ) {
          processedUnmatched.push(column);
          return;
        }

        // Check for base field match (PropertyFields) - but exclude specific business fields
        const excludedBusinessFields = [
          "Year Built / Renovated",
          "Asking Rate (Shell/Office)",
        ];
        const isExcludedBusinessField = excludedBusinessFields.some(
          (excludedField) =>
            column.toLowerCase() === excludedField.toLowerCase(),
        );

        const baseFieldMatch = !isExcludedBusinessField
          ? PropertyFields.find((field) => {
              const fieldLabelLower = field.label.toLowerCase();
              const fieldKeyLower = field.key.toLowerCase();

              // Special handling for car parking
              if (
                lowerColumnName.includes("car") &&
                lowerColumnName.includes("parking") &&
                field.key === "parkingSpacesCars"
              ) {
                return true;
              }

              // Special handling for trailer parking
              if (
                lowerColumnName.includes("trailer") &&
                lowerColumnName.includes("parking") &&
                field.key === "parkingSpacesTrailers"
              ) {
                return true;
              }

              return (
                fieldLabelLower === lowerColumnName ||
                fieldKeyLower === lowerColumnName ||
                fieldLabelLower.includes(lowerColumnName) ||
                lowerColumnName.includes(fieldLabelLower) ||
                fieldKeyLower.includes(lowerColumnName) ||
                lowerColumnName.includes(fieldKeyLower)
              );
            })
          : null;

        // Check for business field mapping match
        const businessFieldMatch = Object.entries(BusinessFieldMappings).find(
          ([businessHeader]) =>
            businessHeader.toLowerCase() === lowerColumnName ||
            column === businessHeader,
        );

        let businessFieldPropertyMatch = null;
        if (businessFieldMatch) {
          const [, targetField] = businessFieldMatch;
          businessFieldPropertyMatch = PropertyFields.find(
            (field) => field.key === targetField,
          );
        }

        // Check for custom field match
        const customFieldMatch = customFieldOptions.find((option) => {
          const optionLabelLower = option.label.toLowerCase();
          return (
            optionLabelLower === lowerColumnName ||
            optionLabelLower.includes(lowerColumnName) ||
            lowerColumnName.includes(optionLabelLower)
          );
        });

        // Determine what the default value would be for this column
        const defaultValue = findMatchedField(column);

        // Only put columns that have actual matches (not CUSTOM_FIELD_IMPORT) in matched section
        if (
          (baseFieldMatch || businessFieldPropertyMatch || customFieldMatch) &&
          defaultValue !== "CUSTOM_FIELD_IMPORT"
        ) {
          processedMatched.push(column);
        } else {
          // Custom field imports and truly unmappable columns go to unmatched section
          // Only add to unmatched if the column is actually present in the Excel file
          const isActualExcelColumn =
            importResult && importResult.unParsedColumns[0].includes(column);

          if (isActualExcelColumn) {
            processedUnmatched.push(column);
          }
        }
      });

      setOriginalMatchedColumns(processedMatched);
      setOriginalUnmatchedColumns(processedUnmatched);
    }
  }, [importResult?.matched, importResult?.notMatched, customFieldOptions]);

  if (!importResult) return null;

  // Check for multiple blank headers and show alert if found
  const allColumns = [...importResult.matched, ...importResult.notMatched];
  const blankHeaderCount = allColumns.filter(isBlankColumn).length;

  if (blankHeaderCount >= 2) {
    return (
      <EmeraldModal
        opened={isOpen}
        title="Import Error"
        onClose={handleCancel}
        headerText="Import Error"
        hasCloseIcon={true}
        className="column-matching-modal"
        actionTemplate={
          <EmeraldButton
            label="Close"
            type="Primary"
            onClick={handleCancel}
            className="mdc-dialog__button"
            color="primary"
          />
        }
      >
        <div className="flex flex-col max-h-[80vh]">
          <div className="px-6 py-4 column-matching-modal-notifications">
            <EmeraldNotification
              variant="error"
              closable={false}
              title="Multiple Blank Column Headers Detected"
              message="The file you are trying to import has more than one empty/blank column headers. Please revisit the file."
            />
          </div>
        </div>
      </EmeraldModal>
    );
  }

  return (
    <EmeraldModal
      opened={isOpen}
      title="Import from Excel"
      onClose={handleCancel}
      headerText="Import from Excel"
      hasCloseIcon={true}
      className="column-matching-modal"
      actionTemplate={
        <>
          <EmeraldButton
            label="Cancel"
            type="Secondary"
            onClick={handleCancel}
            disabled={isProcessing}
            className="mdc-dialog__button"
            color="primary"
          />
          <EmeraldButton
            label={isProcessing ? "Processing..." : "Next"}
            type="Primary"
            onClick={handleNext}
            disabled={isProcessing || !isNextEnabled}
            className="mdc-dialog__button"
            color="primary"
          />
        </>
      }
    >
      <div className="flex flex-col max-h-[80vh]">
        {/* Progress Header */}
        <div className="px-6">
          {/* Progress Indicator */}
          <EmeraldStepperV2
            onClick={function Ga() {}}
            orientation="horizontal"
            steps={[
              {
                id: "1",
                label: " Match Columns",
                state: "current",
              },
              {
                id: "2",
                label: "Row Report",
                state: "upcoming",
              },
            ]}
          />

          <p className="text-sm text-data">
            Must map either: Address Line 1, City, and State together, OR
            Latitude and Longitude together. Each field can only be mapped once.
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 p-4">
          <div className="grid grid-cols-2 gap-6">
            {/* Matched Columns Section */}
            <div>
              <div className="mb-4 column-matching-modal-notifications">
                <EmeraldNotification
                  variant="success"
                  closable={false}
                  title={`${filteredColumnCategories.matched.length} Columns matched`}
                  message={`Nice! We were able to match ${filteredColumnCategories.matched.length} of ${filteredColumnCategories.matched.length + filteredColumnCategories.notMatched.length} columns in your file.`}
                />
              </div>

              <div className="flex flex-col gap-y-6">
                <div className="flex pb-4 items-center gap-2.5 border-b border-gray-300 text-sm font-medium text-gray-900">
                  <div className="flex-1 text-data">Your Column Title</div>
                  <div className="flex-1 text-data">Our Match</div>
                </div>

                {filteredColumnCategories.matched.map((column) => {
                  const columnValue = getColumnValue(column);
                  const availableOptions = getAvailableOptions();

                  return (
                    <div key={column} className="grid grid-cols-2 items-center">
                      <ColumnNameDisplay columnName={column} />
                      <div>
                        <SearchableSelectWithClear
                          column={column}
                          value={columnValue}
                          options={availableOptions}
                          onChange={handleColumnMapping}
                          onClear={handleClearMapping}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Unmatched Columns Section */}
            <div>
              <div className="mb-4 column-matching-modal-notifications">
                <EmeraldNotification
                  variant="warning"
                  closable={false}
                  title={`${filteredColumnCategories.notMatched.length} Columns to match`}
                  message={`Help us match these last ${filteredColumnCategories.notMatched.length} columns by using the dropdown menus.`}
                />
              </div>

              <div className="flex flex-col gap-y-6">
                <div className="flex pb-4 items-center gap-2.5 border-b border-gray-300 text-sm font-medium text-gray-900">
                  <div className="flex-1 text-data">Your Column Title</div>
                  <div className="flex-1 text-data">Our Match</div>
                </div>

                {filteredColumnCategories.notMatched.map((column) => {
                  const columnValue = getColumnValue(column);
                  const availableOptions = getAvailableOptions();

                  return (
                    <div key={column} className="grid grid-cols-2 items-center">
                      <ColumnNameDisplay columnName={column} />
                      <div>
                        <SearchableSelectWithClear
                          column={column}
                          value={columnValue}
                          options={availableOptions}
                          onChange={handleColumnMapping}
                          onClear={handleClearMapping}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </EmeraldModal>
  );
};

export default ColumnMatchingModal;
